import React, { useState } from 'react';

interface SessionColors {
  preMarket: string;
  regular: string;
  afterHours: string;
}

interface ChartSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  sessionColors: SessionColors;
  onSessionColorsChange: (colors: SessionColors) => void;
}

const ChartSettingsPanel: React.FC<ChartSettingsPanelProps> = ({
  isOpen,
  onClose,
  sessionColors,
  onSessionColorsChange,
}) => {
  if (!isOpen) return null;

  const handleColorChange = (key: keyof SessionColors, value: string) => {
    onSessionColorsChange({ ...sessionColors, [key]: value });
  };

  const [backgroundType, setBackgroundType] = useState<'solid' | 'gradient'>('gradient');
  const [backgroundPrimary, setBackgroundPrimary] = useState<string>('#020617');
  const [backgroundSecondary, setBackgroundSecondary] = useState<string>('#0f172a');
  const [gridMode, setGridMode] = useState<'none' | 'vertical' | 'horizontal' | 'both'>('both');
  const [crosshairColor, setCrosshairColor] = useState<string>('#64748b');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-slate-900 rounded-2xl border border-slate-800 shadow-2xl w-full max-w-3xl max-h-[80vh] flex overflow-hidden">
        {/* Left nav */}
        <div className="w-48 border-r border-slate-800 bg-slate-950/80 p-3 space-y-1 text-sm">
          <div className="text-xs font-semibold text-slate-500 px-2 mb-1">Settings</div>
          <button className="w-full text-left px-2 py-1.5 rounded-lg bg-slate-800 text-slate-100 text-xs font-medium flex items-center gap-2">
            <span className="w-1 h-4 rounded bg-emerald-500" />
            Chart
          </button>
          <button className="w-full text-left px-2 py-1.5 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-slate-800/70 text-xs font-medium flex items-center gap-2">
            <span className="w-1 h-4 rounded bg-slate-700" />
            Sessions
          </button>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <div>
              <h2 className="text-sm font-semibold text-slate-100">Chart Settings</h2>
              <p className="text-xs text-slate-500">Configure session highlighting and other chart options.</p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-slate-100"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-6 text-sm">
            {/* Canvas / Appearance section */}
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Canvas
              </div>
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 space-y-4">
                <div className="space-y-2">
                  <div className="text-[11px] font-medium text-slate-400">Background</div>
                  <div className="flex flex-wrap items-center gap-3">
                    <select
                      value={backgroundType}
                      onChange={(e) => setBackgroundType(e.target.value as 'solid' | 'gradient')}
                      className="bg-slate-900 border border-slate-700 text-[11px] text-slate-200 rounded px-2 py-1 outline-none"
                    >
                      <option value="solid">Solid</option>
                      <option value="gradient">Gradient</option>
                    </select>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-500">From</span>
                      <input
                        type="color"
                        value={backgroundPrimary}
                        onChange={(e) => setBackgroundPrimary(e.target.value)}
                        className="w-7 h-7 rounded border border-slate-700 bg-slate-900 cursor-pointer"
                      />
                    </div>
                    {backgroundType === 'gradient' && (
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-slate-500">To</span>
                        <input
                          type="color"
                          value={backgroundSecondary}
                          onChange={(e) => setBackgroundSecondary(e.target.value)}
                          className="w-7 h-7 rounded border border-slate-700 bg-slate-900 cursor-pointer"
                        />
                      </div>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    These colors control the visual canvas background only and do not yet change the live chart.
                  </p>
                </div>

                <div className="border-t border-slate-800 pt-3 mt-3 space-y-2">
                  <div className="text-[11px] font-medium text-slate-400">Grid lines</div>
                  <div className="flex flex-wrap items-center gap-2">
                    {(['none', 'vertical', 'horizontal', 'both'] as const).map((mode) => (
                      <button
                        key={mode}
                        onClick={() => setGridMode(mode)}
                        className={`px-2 py-1 rounded text-[11px] border transition-colors ${
                          gridMode === mode
                            ? 'bg-slate-800 border-emerald-500/70 text-emerald-300'
                            : 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500'
                        }`}
                      >
                        {mode === 'none' && 'None'}
                        {mode === 'vertical' && 'Vert only'}
                        {mode === 'horizontal' && 'Horz only'}
                        {mode === 'both' && 'Vert and horz'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-slate-800 pt-3 mt-3 space-y-2">
                  <div className="text-[11px] font-medium text-slate-400">Crosshair</div>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] text-slate-500 max-w-xs">
                      Adjust the crosshair color used for chart inspection. This is currently a visual preference only.
                    </p>
                    <input
                      type="color"
                      value={crosshairColor}
                      onChange={(e) => setCrosshairColor(e.target.value)}
                      className="w-8 h-8 rounded border border-slate-700 bg-slate-900 cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Session Highlighter section */}
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Session Highlighter
              </div>
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 space-y-3">
                <p className="text-xs text-slate-400">
                  Choose highlight colors for pre-market, regular, and after-hours sessions. These tints will appear
                  as subtle background bands behind the candles.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-slate-200">Pre-market</span>
                      <span className="text-[10px] text-slate-500">04:00–09:30 EST</span>
                    </div>
                    <input
                      type="color"
                      value={sessionColors.preMarket}
                      onChange={(e) => handleColorChange('preMarket', e.target.value)}
                      className="w-8 h-8 rounded border border-slate-700 bg-slate-900 cursor-pointer"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-slate-200">Regular</span>
                      <span className="text-[10px] text-slate-500">09:30–16:00 EST</span>
                    </div>
                    <input
                      type="color"
                      value={sessionColors.regular}
                      onChange={(e) => handleColorChange('regular', e.target.value)}
                      className="w-8 h-8 rounded border border-slate-700 bg-slate-900 cursor-pointer"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-slate-200">After-hours</span>
                      <span className="text-[10px] text-slate-500">16:00–20:00 EST</span>
                    </div>
                    <input
                      type="color"
                      value={sessionColors.afterHours}
                      onChange={(e) => handleColorChange('afterHours', e.target.value)}
                      className="w-8 h-8 rounded border border-slate-700 bg-slate-900 cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-800 bg-slate-950/80">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartSettingsPanel;
